import { useEffect, useMemo, useState } from "react";
import type {
  HostPlan,
  PanelUser,
  ProvisionJob,
  ServerNode,
  SshKey
} from "../types";
import {
  CreateAccountForm,
  type CreateAccountPayload
} from "./CreateAccountForm";
import {
  ModifyAccountForm,
  type ModifyAccountPayload
} from "./ModifyAccountForm";

/**
 * W2 — WHM Account Functions:
 * List Accounts · Create a New Account · Modify · Suspend · Terminate · Log in to cPanel
 */
export function AccountsPage({
  accounts,
  plans,
  packages,
  servers: _servers = [],
  currentUser,
  openCreate,
  onOpenCreateConsumed,
  onCreate,
  onUpdate,
  onSuspend,
  onUnsuspend,
  onDelete,
  onImpersonate,
  onProvision,
  onRefreshUsage,
  onMassAction,
  onLoadDetail,
  onAddSshKey,
  onRemoveSshKey,
  onSetSshAccess: _onSetSshAccess,
  onResetPassword: _onResetPassword,
  onRefresh
}: {
  accounts: PanelUser[];
  /** Hosting Plans (resources) assigned to accounts */
  plans?: HostPlan[];
  /** @deprecated use plans */
  packages?: HostPlan[];
  currentUser: PanelUser;
  servers?: ServerNode[];
  openCreate?: boolean;
  onOpenCreateConsumed?: () => void;
  onCreate: (
    data: CreateAccountPayload
  ) => Promise<{ setupError?: string; domainError?: string } | void>;
  onUpdate?: (
    id: string,
    data: Record<string, unknown>
  ) => Promise<void>;
  onSuspend: (id: string) => Promise<void>;
  onUnsuspend: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onImpersonate: (id: string) => Promise<void>;
  onProvision: (id: string) => Promise<void>;
  onRefreshUsage: (id: string) => Promise<void>;
  onAssignServer?: (id: string, serverId: string | null) => Promise<void>;
  onMassAction?: (
    action: "suspend" | "unsuspend" | "provision" | "delete",
    ids: string[]
  ) => Promise<void>;
  onLoadDetail: (
    id: string
  ) => Promise<{ account: PanelUser; sshKeys: SshKey[]; jobs: ProvisionJob[] }>;
  onAddSshKey: (id: string, name: string, publicKey: string) => Promise<void>;
  onRemoveSshKey: (id: string, keyId: string) => Promise<void>;
  onSetSshAccess: (
    id: string,
    sshAccess: boolean,
    sftpOnly: boolean
  ) => Promise<void>;
  onResetPassword?: (id: string, password: string) => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const reseller = currentUser.role === "reseller";
  const canManageAccounts = staff || reseller;
  const hostingPlans = (plans && plans.length ? plans : packages || []) as HostPlan[];
  const [showCreate, setShowCreate] = useState(Boolean(openCreate));
  const [mode, setMode] = useState<"list" | "create" | "detail">(
    openCreate ? "create" : "list"
  );

  useEffect(() => {
    if (openCreate) {
      setShowCreate(true);
      setMode("create");
      onOpenCreateConsumed?.();
    }
  }, [openCreate, onOpenCreateConsumed]);

  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "suspended" | "setup"
  >("all");
  const [selected, setSelected] = useState<PanelUser | null>(null);
  const [sshKeys, setSshKeys] = useState<SshKey[]>([]);
  const [jobs, setJobs] = useState<ProvisionJob[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [packageFilter, setPackageFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");

  const setupLabel = (status: string | undefined) => {
    switch (status) {
      case "ready":
        return { text: "Ready", tone: "ok" as const };
      case "provisioning":
        return { text: "Setting up…", tone: "warn" as const };
      case "failed":
        return { text: "Setup failed", tone: "crit" as const };
      case "none":
      case "pending":
        return { text: "Pending", tone: "warn" as const };
      default:
        return { text: status || "—", tone: "muted" as const };
    }
  };

  const packageOptions = useMemo(() => {
    const names = new Set<string>();
    for (const a of accounts) {
      if (a.packageName) names.add(a.packageName);
    }
    return Array.from(names).sort();
  }, [accounts]);

  const ownerOptions = useMemo(() => {
    const names = new Set<string>();
    for (const a of accounts) {
      if (a.ownerUsername) names.add(a.ownerUsername);
    }
    return Array.from(names).sort();
  }, [accounts]);

  const filtered = useMemo(() => {
    let list = accounts.filter(
      (a) => (a.role === "user" || staff) && a.status !== "deleted"
    );
    list = list.filter((a) => {
      if (statusFilter === "active")
        return a.status === "active" && a.provisionStatus === "ready";
      if (statusFilter === "suspended") return a.status === "suspended";
      if (statusFilter === "setup")
        return (
          a.role === "user" &&
          a.provisionStatus !== "ready" &&
          a.status !== "deleted"
        );
      return true;
    });
    if (packageFilter !== "all") {
      list = list.filter((a) => a.packageName === packageFilter);
    }
    if (ownerFilter !== "all") {
      list = list.filter(
        (a) => (a.ownerUsername || "root") === ownerFilter
      );
    }
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(
      (a) =>
        a.username.includes(s) ||
        (a.email || "").toLowerCase().includes(s) ||
        (a.primaryDomain || "").toLowerCase().includes(s) ||
        (a.packageName || "").toLowerCase().includes(s) ||
        (a.ownerUsername || "").toLowerCase().includes(s) ||
        a.role.includes(s) ||
        a.status.includes(s)
    );
  }, [accounts, q, statusFilter, staff, packageFilter, ownerFilter]);

  const openDetail = async (a: PanelUser) => {
    setError("");
    setMsg("");
    try {
      const d = await onLoadDetail(a.id);
      setSelected(d.account);
      setSshKeys(d.sshKeys);
      setJobs(d.jobs);
      setMode("detail");
      setShowCreate(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const submitCreate = async (data: CreateAccountPayload) => {
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await onCreate(data);
      setShowCreate(false);
      setMode("list");
      if (res?.setupError) {
        setError(`Account created but setup failed: ${res.setupError}`);
      } else if (res?.domainError) {
        setMsg(
          `Account ready. Domain setup issue: ${res.domainError} — add domain under Sites later.`
        );
      } else {
        setMsg("Account created successfully.");
      }
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const submitModify = async (data: ModifyAccountPayload) => {
    if (!selected || !onUpdate) return;
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await onUpdate(selected.id, data as Record<string, unknown>);
      setMsg("Account updated.");
      await openDetail(selected);
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const userAccounts = accounts.filter((a) => a.role === "user");

  const activeCount = userAccounts.filter(
    (a) => a.status === "active" && a.provisionStatus === "ready"
  ).length;
  const suspendedCount = userAccounts.filter(
    (a) => a.status === "suspended"
  ).length;
  const failedCount = userAccounts.filter(
    (a) => a.provisionStatus === "failed"
  ).length;

  const runBulk = async (
    action: "suspend" | "unsuspend" | "provision" | "delete"
  ) => {
    if (!selectedIds.length) return;
    if (
      action === "delete" &&
      !confirm(
        `Delete ${selectedIds.length} selected account(s)? This cannot be undone.`
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (onMassAction && action !== "delete") {
        await onMassAction(action, selectedIds);
      } else if (action === "delete") {
        if (onMassAction) await onMassAction("delete", selectedIds);
        else {
          for (const id of selectedIds) {
            if (id !== currentUser.id) await onDelete(id);
          }
        }
      }
      setSelectedIds([]);
      setMsg(
        action === "delete"
          ? "Selected accounts deleted."
          : `Bulk ${action} complete.`
      );
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="rp-page">
        <div className="rp-page-head">
          <div>
            <h1 className="rp-page-title">
              {mode === "create"
                ? "Create Account"
                : mode === "detail"
                  ? "Modify Account"
                  : "Accounts"}
            </h1>
            <p className="rp-page-sub">
              {mode === "create"
                ? "Provision a hosting account with plan, resources and access settings."
                : mode === "detail"
                  ? "Update package, contact email, shell access, or password."
                  : "Manage hosting accounts, packages, access and account status."}
            </p>
          </div>
          <div className="rp-page-actions">
            {mode !== "list" ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setMode("list");
                  setShowCreate(false);
                  setSelected(null);
                  setMenuId(null);
                }}
              >
                ← Accounts
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void onRefresh()}
            >
              Refresh
            </button>
            {canManageAccounts && mode === "list" ? (
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: "auto", marginTop: 0 }}
                onClick={() => {
                  setMode("create");
                  setShowCreate(true);
                }}
              >
                + Create Account
              </button>
            ) : null}
          </div>
        </div>

        {msg ? <div className="success-banner">{msg}</div> : null}
        {error ? <div className="error-banner">{error}</div> : null}

        {mode === "create" && showCreate ? (
          <CreateAccountForm
            plans={hostingPlans}
            accounts={accounts}
            currentUser={currentUser}
            busy={busy}
            onSubmit={submitCreate}
            onCancel={() => {
              setMode("list");
              setShowCreate(false);
            }}
          />
        ) : null}

        {mode === "list" ? (
          <>
            <div className="rp-summary-strip">
              <div className="rp-sum">
                <span className="rp-sum-label">Hosting Accounts</span>
                <span className="rp-sum-value">{userAccounts.length}</span>
                <span className="rp-sum-hint">Total end-user accounts</span>
              </div>
              <div className="rp-sum">
                <span className="rp-sum-label">Active</span>
                <span className="rp-sum-value">{activeCount}</span>
                <span className="rp-sum-hint">Ready &amp; online</span>
              </div>
              <div className="rp-sum">
                <span className="rp-sum-label">Suspended</span>
                <span className="rp-sum-value">{suspendedCount}</span>
                <span className="rp-sum-hint">Access blocked</span>
              </div>
              <div className="rp-sum">
                <span className="rp-sum-label">Setup Failed</span>
                <span className="rp-sum-value">{failedCount}</span>
                <span className="rp-sum-hint">Needs retry</span>
              </div>
            </div>

            <div className="rp-toolbar">
              <input
                className="rp-toolbar-search"
                placeholder="Search accounts, domains, packages…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value as "all" | "active" | "suspended" | "setup"
                  )
                }
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="setup">Needs setup</option>
              </select>
              <select
                value={packageFilter}
                onChange={(e) => setPackageFilter(e.target.value)}
              >
                <option value="all">All packages</option>
                {packageOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <select
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
              >
                <option value="all">All owners</option>
                <option value="root">root</option>
                {ownerOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              {canManageAccounts ? (
                selectedIds.length === 0 ? (
                  <span className="rp-bulk-hint">
                    Select accounts to enable bulk actions
                  </span>
                ) : (
                  <div className="rp-bulk-bar">
                    {staff && onMassAction ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => void runBulk("suspend")}
                        >
                          Suspend
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => void runBulk("unsuspend")}
                        >
                          Unsuspend
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => void runBulk("provision")}
                        >
                          Retry Setup
                        </button>
                      </>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      disabled={busy || selectedIds.includes(currentUser.id)}
                      onClick={() => void runBulk("delete")}
                    >
                      Delete ({selectedIds.length})
                    </button>
                  </div>
                )
              ) : null}
            </div>

            <div className="rp-panel">
              <div className="table-wrap" style={{ border: "none", boxShadow: "none" }}>
                <table className="rp-acct-table">
                  <thead>
                    <tr>
                      {canManageAccounts ? (
                        <th style={{ width: 40 }}>
                          <input
                            type="checkbox"
                            aria-label="Select all"
                            checked={
                              filtered.filter(
                                (a) =>
                                  a.role === "user" && a.id !== currentUser.id
                              ).length > 0 &&
                              filtered
                                .filter(
                                  (a) =>
                                    a.role === "user" && a.id !== currentUser.id
                                )
                                .every((a) => selectedIds.includes(a.id))
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(
                                  filtered
                                    .filter(
                                      (a) =>
                                        a.role === "user" &&
                                        a.id !== currentUser.id
                                    )
                                    .map((a) => a.id)
                                );
                              } else setSelectedIds([]);
                            }}
                          />
                        </th>
                      ) : null}
                      <th>Account</th>
                      <th>Domain</th>
                      <th>Status</th>
                      <th>Setup</th>
                      <th>Usage</th>
                      <th>Plan</th>
                      {canManageAccounts ? <th style={{ width: 48 }} /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td
                          colSpan={canManageAccounts ? 8 : 6}
                          style={{ textAlign: "center", padding: 40 }}
                        >
                          <strong style={{ display: "block", marginBottom: 6 }}>
                            No hosting accounts found
                          </strong>
                          <span className="faint">
                            Adjust filters or create your first account.
                          </span>
                        </td>
                      </tr>
                    ) : (
                      filtered.map((a) => {
                        const quota = a.quotaDiskMb || 0;
                        const used = a.diskUsedMb || 0;
                        const pct =
                          quota > 0
                            ? Math.min(100, Math.round((used / quota) * 100))
                            : 0;
                        const s = setupLabel(a.provisionStatus);
                        const usageLabel =
                          quota > 0
                            ? `${formatDisk(used)} / ${formatDisk(quota)}`
                            : used
                              ? formatDisk(used)
                              : "—";
                        return (
                          <tr key={a.id}>
                            {canManageAccounts ? (
                              <td>
                                {a.role === "user" &&
                                a.id !== currentUser.id ? (
                                  <input
                                    type="checkbox"
                                    checked={selectedIds.includes(a.id)}
                                    onChange={(e) => {
                                      setSelectedIds((prev) =>
                                        e.target.checked
                                          ? [...prev, a.id]
                                          : prev.filter((x) => x !== a.id)
                                      );
                                    }}
                                  />
                                ) : null}
                              </td>
                            ) : null}
                            <td>
                              <div className="rp-acct-user">
                                <strong>{a.username}</strong>
                                <small>
                                  {a.ownerUsername || (staff ? "root" : "—")}
                                  {a.role !== "user" ? ` · ${a.role}` : ""}
                                </small>
                              </div>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="link-btn"
                                onClick={() => void openDetail(a)}
                              >
                                {a.primaryDomain || (
                                  <span className="faint">—</span>
                                )}
                              </button>
                            </td>
                            <td>
                              <span
                                className={`rp-status rp-status-${
                                  a.status === "active"
                                    ? "ok"
                                    : a.status === "suspended"
                                      ? "crit"
                                      : "muted"
                                }`}
                              >
                                <span className="rp-status-dot" />
                                {a.status.charAt(0).toUpperCase() +
                                  a.status.slice(1)}
                              </span>
                            </td>
                            <td>
                              {a.role === "user" ? (
                                <span
                                  className={`rp-status rp-status-${s.tone}`}
                                >
                                  <span className="rp-status-dot" />
                                  {s.text}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              {a.provisionStatus === "ready" ? (
                                <div className="rp-usage">
                                  <span className="rp-usage-text">
                                    {usageLabel}
                                  </span>
                                  {quota > 0 ? (
                                    <div className="rp-usage-bar">
                                      <div
                                        className={`rp-usage-fill${
                                          pct >= 90
                                            ? " crit"
                                            : pct >= 75
                                              ? " warn"
                                              : ""
                                        }`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              ) : (
                                <span className="faint">—</span>
                              )}
                            </td>
                            <td>{a.packageName || "—"}</td>
                            {canManageAccounts ? (
                              <td>
                                <div className="rp-menu-wrap">
                                  <button
                                    type="button"
                                    className="rp-menu-btn"
                                    aria-label="Actions"
                                    onClick={() =>
                                      setMenuId((id) =>
                                        id === a.id ? null : a.id
                                      )
                                    }
                                  >
                                    •••
                                  </button>
                                  {menuId === a.id ? (
                                    <div className="rp-menu">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setMenuId(null);
                                          void openDetail(a);
                                        }}
                                      >
                                        Modify
                                      </button>
                                      {a.role === "user" ? (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setMenuId(null);
                                            void onImpersonate(a.id);
                                          }}
                                        >
                                          Login to cPanel
                                        </button>
                                      ) : null}
                                      {a.status === "active" ? (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setMenuId(null);
                                            void onSuspend(a.id);
                                          }}
                                        >
                                          Suspend
                                        </button>
                                      ) : a.status === "suspended" ? (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setMenuId(null);
                                            void onUnsuspend(a.id);
                                          }}
                                        >
                                          Unsuspend
                                        </button>
                                      ) : null}
                                      {a.role === "user" &&
                                      a.provisionStatus !== "ready" ? (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setMenuId(null);
                                            void onProvision(a.id);
                                          }}
                                        >
                                          Retry Setup
                                        </button>
                                      ) : null}
                                      {a.id !== currentUser.id ? (
                                        <button
                                          type="button"
                                          className="danger"
                                          onClick={() => {
                                            setMenuId(null);
                                            if (
                                              confirm(
                                                `Delete account ${a.username}?`
                                              )
                                            )
                                              void onDelete(a.id);
                                          }}
                                        >
                                          Delete
                                        </button>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </div>
                              </td>
                            ) : null}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}

        {/* —— Modify / detail —— */}
        {mode === "detail" && selected && canManageAccounts && onUpdate ? (
        <ModifyAccountForm
          account={selected}
          plans={hostingPlans}
          sshKeys={sshKeys}
          jobs={jobs}
          busy={busy}
          currentUserId={currentUser.id}
          onSubmit={submitModify}
          onCancel={() => {
            setSelected(null);
            setMode("list");
          }}
          onImpersonate={
            (staff || reseller) &&
            selected.role === "user" &&
            selected.status === "active" &&
            selected.provisionStatus === "ready"
              ? () => void onImpersonate(selected.id)
              : undefined
          }
          onSuspend={
            selected.status === "active" &&
            selected.id !== currentUser.id &&
            selected.role === "user"
              ? () => {
                  if (confirm(`Suspend ${selected.username}?`)) {
                    void onSuspend(selected.id).then(() => {
                      void openDetail(selected);
                      void onRefresh();
                    });
                  }
                }
              : undefined
          }
          onUnsuspend={
            selected.status === "suspended"
              ? () =>
                  void onUnsuspend(selected.id).then(() => {
                    void openDetail(selected);
                    void onRefresh();
                  })
              : undefined
          }
          onDelete={
            selected.id !== currentUser.id
              ? () => {
                  if (
                    confirm(
                      `Delete account “${selected.username}”? This removes the panel account and system home. This cannot be undone.`
                    )
                  ) {
                    void onDelete(selected.id).then(() => {
                      setSelected(null);
                      setMode("list");
                      void onRefresh();
                    });
                  }
                }
              : undefined
          }
          onProvision={
            selected.role === "user" && selected.provisionStatus !== "ready"
              ? () =>
                  void onProvision(selected.id).then(() => openDetail(selected))
              : undefined
          }
          onRefreshUsage={
            selected.provisionStatus === "ready"
              ? () =>
                  void onRefreshUsage(selected.id).then(() =>
                    openDetail(selected)
                  )
              : undefined
          }
          onAddSshKey={async (name, pub) => {
            await onAddSshKey(selected.id, name, pub);
            await openDetail(selected);
          }}
          onRemoveSshKey={async (keyId) => {
            await onRemoveSshKey(selected.id, keyId);
            await openDetail(selected);
          }}
        />
        ) : null}
      </div>
    </>
  );
}

function formatDisk(mb: number): string {
  if (!Number.isFinite(mb) || mb < 0) return "—";
  if (mb < 1024) return `${Math.round(mb)} MB`;
  const gb = mb / 1024;
  if (gb < 1024) return `${gb < 10 ? gb.toFixed(1) : Math.round(gb)} GB`;
  return `${(gb / 1024).toFixed(1)} TB`;
}
